import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Decal, useGLTF } from '@react-three/drei';
import { useFrame, useThree } from '@react-three/fiber';
import * as THREE from 'three';
import {
  KIT,
  createNamePlateTexture,
  createNumberPlateTexture,
  createCollarBandTexture,
  createCuffTexture,
} from './jerseyCanvas';
import {
  JerseyDustParticles,
  applyClothDissolveShader,
} from './JerseyClothDissolve';
import { createSoftClothTextures } from './softClothTextures';
import { SHIRT_GLB_URL } from './lockerUrls';
import { useIsMobile } from '../../core/useIsMobile';

export { SHIRT_GLB_URL };

type Props = {
  name?: string;
  number?: number;
  height?: number;
  /** When false, only the empty hanger remains on the rod. */
  showShirt?: boolean;
  /** 0→1 cloth dissolve (bottom→top). Hanger fades out on the same clock. */
  dissolve?: number;
};

function findMesh(root: THREE.Object3D, ...needles: string[]): THREE.Mesh | null {
  let hit: THREE.Mesh | null = null;
  const lower = needles.map((n) => n.toLowerCase());
  root.traverse((o) => {
    if (hit || !(o as THREE.Mesh).isMesh) return;
    const n = o.name.toLowerCase();
    if (lower.some((needle) => n === needle || n.includes(needle))) {
      hit = o as THREE.Mesh;
    }
  });
  return hit;
}

function goldMat() {
  return new THREE.MeshStandardMaterial({
    color: '#e8c892',
    metalness: 0.92,
    roughness: 0.18,
    envMapIntensity: 1.1,
  });
}

/**
 * Hook opens around the X-axis rod (torus hole along X).
 * Fades out with the jersey dissolve (same timing).
 */
function RodHook({ dissolve = 0 }: { dissolve?: number }) {
  const gold = '#e8c892';
  const opacity = Math.max(0, 1 - dissolve);
  if (opacity <= 0.02) return null;
  return (
    <group>
      <mesh
        rotation={[0.55, Math.PI / 2 + 0.35, 0]}
        position={[0, 0.006, 0]}
        castShadow={opacity > 0.35}
      >
        <torusGeometry args={[0.042, 0.011, 14, 40, Math.PI * 1.58]} />
        <meshStandardMaterial
          color={gold}
          metalness={0.92}
          roughness={0.16}
          envMapIntensity={1.15}
          transparent
          opacity={opacity}
          depthWrite={opacity > 0.85}
        />
      </mesh>
      <mesh position={[0, -0.11, 0]} castShadow={opacity > 0.35}>
        <cylinderGeometry args={[0.008, 0.01, 0.2, 12]} />
        <meshStandardMaterial
          color={gold}
          metalness={0.9}
          roughness={0.2}
          envMapIntensity={1.1}
          transparent
          opacity={opacity}
          depthWrite={opacity > 0.85}
        />
      </mesh>
    </group>
  );
}

/**
 * Shirt + baked hanger. Name/number are Decals projected onto the cloth
 * (follow surface — no floating grey plates).
 */
export function FootballShirt({
  name = 'PLAYER',
  number = 7,
  height = 1.2,
  showShirt = true,
  dissolve = 0,
}: Props) {
  const { scene } = useGLTF(SHIRT_GLB_URL);
  const mobile = useIsMobile();
  const { gl } = useThree();
  const dissolveU = useRef({
    uDissolve: { value: 0 },
    uYMin: { value: -1 },
    uYMax: { value: 1 },
  });
  const shirtMatRef = useRef<THREE.MeshStandardMaterial | null>(null);
  const decalMats = useRef<THREE.MeshStandardMaterial[]>([]);
  const shaderHooked = useRef(false);

  const parts = useMemo(() => {
    const clone = scene.clone(true);

    const shirt = findMesh(clone, 'shirt');
    const hangerA = findMesh(clone, 'hanger_a');
    const hangerB = findMesh(clone, 'hanger_b');

    // Flat placeholder planes — never show these
    for (const needle of ['nameplate', 'numberplate']) {
      const m = findMesh(clone, needle);
      if (m) m.visible = false;
    }

    const gold = goldMat();
    // Shoulder bar stays; baked hook (Hanger_B) faces wrong axis — hide it
    if (hangerA) {
      hangerA.visible = true;
      hangerA.material = gold;
      hangerA.castShadow = true;
      hangerA.receiveShadow = true;
      hangerA.frustumCulled = false;
      hangerA.scale.setScalar(1.12);
    }
    if (hangerB) {
      // Wrong-axis baked hook — only show when shirt is gone (empty hanger)
      hangerB.visible = false;
      hangerB.material = gold;
      hangerB.castShadow = true;
      hangerB.receiveShadow = true;
      hangerB.frustumCulled = false;
    }

    if (!shirt) {
      return {
        hangers: clone,
        shirtGeo: null as THREE.BufferGeometry | null,
        shirtMat: null as THREE.Material | null,
        lift: 0.4,
        yMin: -0.8,
        yMax: 0.2,
        namePos: [0, -0.35, 0.05] as [number, number, number],
        numPos: [0, -0.65, 0.05] as [number, number, number],
        collarPos: [0, -0.22, 0.05] as [number, number, number],
        cuffL: [-0.35, -0.4, 0.02] as [number, number, number],
        cuffR: [0.35, -0.4, 0.02] as [number, number, number],
        nameScale: [-0.48, 0.22, 0.18] as [number, number, number],
        numScale: [-0.36, 0.4, 0.18] as [number, number, number],
        collarScale: [-0.25, 0.04, 0.15] as [number, number, number],
        cuffScale: [-0.12, 0.07, 0.15] as [number, number, number],
      };
    }

    const shirtMat = Array.isArray(shirt.material)
      ? shirt.material[0].clone()
      : (shirt.material as THREE.Material).clone();

    // Smooth cloth — higher-res fabric; mobile gets punchier response under stall lights
    if ((shirtMat as THREE.MeshStandardMaterial).isMeshStandardMaterial) {
      const sm = shirtMat as THREE.MeshStandardMaterial;
      const cloth = createSoftClothTextures(1024);
      sm.map = cloth.map;
      sm.roughnessMap = cloth.roughnessMap;
      sm.metalnessMap = null;
      sm.normalMap = cloth.normalMap;
      sm.aoMap = cloth.aoMap;
      sm.aoMapIntensity = mobile ? 0.2 : 0.16;
      sm.color = new THREE.Color(mobile ? '#fcfdff' : KIT.white);
      sm.roughness = mobile ? 0.74 : 0.82;
      sm.metalness = 0;
      sm.envMapIntensity = mobile ? 0.62 : 0.4;
      sm.normalScale = new THREE.Vector2(
        mobile ? 0.16 : 0.12,
        mobile ? 0.16 : 0.12,
      );
      sm.needsUpdate = true;
    }

    // Bounds while still visible
    const box = new THREE.Box3().setFromObject(shirt);
    const size = box.getSize(new THREE.Vector3());
    const kitBox = new THREE.Box3().setFromObject(clone);
    // Hang origin = rod contact inside the hook (tip sits just above y=0)
    const lift = -kitBox.max.y + 0.004;
    const yMin = box.min.y;
    const yMax = box.max.y;

    shirt.visible = false; // drawn below with Decals as children

    // Local positions on the back (+Z) — name slightly lower (flatter cloth)
    const namePos: [number, number, number] = [
      0,
      box.max.y - size.y * 0.33,
      Math.max(box.max.z * 0.22, 0.03),
    ];
    const numPos: [number, number, number] = [
      0,
      box.max.y - size.y * 0.52,
      Math.max(box.max.z * 0.22, 0.03),
    ];
    // Collar just under neckline
    const collarPos: [number, number, number] = [
      0,
      box.max.y - size.y * 0.06,
      box.max.z * 0.2,
    ];
    // Sleeve cuffs (approx outer back corners)
    const cuffY = box.max.y - size.y * 0.28;
    const cuffX = size.x * 0.42;
    const cuffZL = box.max.z * 0.05;
    // Negative X un-mirrors decal projection on the jersey back
    // Deep Z projector so the name survives the close-up camera push
    const nameScale: [number, number, number] = [
      -(size.x * 0.55),
      size.y * 0.26,
      Math.max(size.z * 2.4, 0.32),
    ];
    const numScale: [number, number, number] = [
      -(size.x * 0.4),
      size.y * 0.36,
      Math.max(size.z * 2.2, 0.3),
    ];
    const collarScale: [number, number, number] = [
      -(size.x * 0.28),
      size.y * 0.045,
      size.z * 0.7,
    ];
    const cuffScale: [number, number, number] = [
      -(size.x * 0.14),
      size.y * 0.08,
      size.z * 0.7,
    ];

    clone.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.frustumCulled = false;
    });

    return {
      hangers: clone,
      shirtGeo: shirt.geometry,
      shirtMat,
      lift,
      yMin,
      yMax,
      namePos,
      numPos,
      collarPos,
      cuffL: [-cuffX, cuffY, cuffZL] as [number, number, number],
      cuffR: [cuffX, cuffY, cuffZL] as [number, number, number],
      nameScale,
      numScale,
      collarScale,
      cuffScale,
    };
  }, [scene, mobile]);

  // Max anisotropy once the GL context is ready (helps mobile close-ups)
  useEffect(() => {
    const maxA = Math.min(16, gl.capabilities.getMaxAnisotropy?.() ?? 8);
    const bump = (tex: THREE.Texture | null | undefined) => {
      if (!tex) return;
      tex.anisotropy = maxA;
      tex.needsUpdate = true;
    };
    const sm = parts.shirtMat as THREE.MeshStandardMaterial | null;
    if (sm?.isMeshStandardMaterial) {
      bump(sm.map);
      bump(sm.roughnessMap);
      bump(sm.normalMap);
      bump(sm.aoMap);
    }
  }, [gl, parts.shirtMat]);

  const label = (name || 'PLAYER').slice(0, 12).toUpperCase();
  const num = String(Math.max(1, Math.min(99, Number(number) || 7)));

  const nameMap = useMemo(() => createNamePlateTexture(label), [label]);
  const numberMap = useMemo(() => createNumberPlateTexture(num), [num]);
  const collarMap = useMemo(() => createCollarBandTexture(), []);
  const cuffMap = useMemo(() => createCuffTexture(), []);

  useEffect(() => {
    const maxA = Math.min(16, gl.capabilities.getMaxAnisotropy?.() ?? 8);
    for (const tex of [nameMap, numberMap, collarMap, cuffMap]) {
      tex.anisotropy = maxA;
      tex.needsUpdate = true;
    }
  }, [gl, nameMap, numberMap, collarMap, cuffMap]);

  useEffect(() => {
    nameMap.needsUpdate = true;
    numberMap.needsUpdate = true;
  }, [nameMap, numberMap, label, num]);

  useEffect(
    () => () => {
      nameMap.dispose();
      numberMap.dispose();
      collarMap.dispose();
      cuffMap.dispose();
    },
    [nameMap, numberMap, collarMap, cuffMap],
  );

  const scale = height / 1.25;
  const clothVisible = showShirt && dissolve < 0.995;
  const dustActive = dissolve > 0.001;
  const hangerOpacity = showShirt ? Math.max(0, 1 - dissolve) : 0;
  const hangerVisible = hangerOpacity > 0.02;

  // Baked hanger bar fades with dissolve (same timing as cloth)
  useEffect(() => {
    parts.hangers.traverse((o) => {
      const n = o.name.toLowerCase();
      if (!(o as THREE.Mesh).isMesh) return;
      const mesh = o as THREE.Mesh;
      if (n.includes('hanger_b')) {
        mesh.visible = false;
        return;
      }
      if (n.includes('hanger_a')) {
        mesh.visible = hangerVisible;
        mesh.scale.setScalar(1.12);
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const m of mats) {
          const sm = m as THREE.MeshStandardMaterial;
          if (!sm.isMeshStandardMaterial) continue;
          sm.transparent = true;
          sm.opacity = hangerOpacity;
          sm.depthWrite = hangerOpacity > 0.85;
          sm.needsUpdate = true;
        }
      }
    });
  }, [parts.hangers, hangerVisible, hangerOpacity]);

  const [printsReady, setPrintsReady] = useState(false);
  const clothDrawn = useRef(false);

  // Hook dissolve shader before first paint so the cloth compiles with the
  // same program we'll use — not a later swap that blanks the jersey.
  useLayoutEffect(() => {
    if (!parts.shirtMat || shaderHooked.current) return;
    if (!(parts.shirtMat as THREE.MeshStandardMaterial).isMeshStandardMaterial)
      return;
    const sm = parts.shirtMat as THREE.MeshStandardMaterial;
    dissolveU.current.uYMin.value = parts.yMin;
    dissolveU.current.uYMax.value = parts.yMax;
    applyClothDissolveShader(sm, dissolveU.current);
    sm.transparent = false;
    sm.depthWrite = true;
    shirtMatRef.current = sm;
    shaderHooked.current = true;
    clothDrawn.current = false;
    setPrintsReady(false);
  }, [parts.shirtMat, parts.yMin, parts.yMax]);

  useEffect(() => {
    clothDrawn.current = false;
    setPrintsReady(false);
  }, [parts.shirtGeo]);

  useFrame(() => {
    dissolveU.current.uDissolve.value = dissolve;
    dissolveU.current.uYMin.value = parts.yMin;
    dissolveU.current.uYMax.value = parts.yMax;
    // Keep cloth opaque during dissolve (discard clips it). Transparent sorting
    // was wiping name/number the moment claim started on mobile.
    if (shirtMatRef.current) {
      shirtMatRef.current.transparent = false;
      shirtMatRef.current.depthWrite = dissolve < 0.92;
    }
    for (const m of decalMats.current) {
      const u = m.userData.dissolveU as
        | {
            uDissolve: { value: number };
            uYMin: { value: number };
            uYMax: { value: number };
            uFixedY?: { value: number };
          }
        | undefined;
      if (!u) continue;
      u.uDissolve.value = dissolve;
      u.uYMin.value = parts.yMin;
      u.uYMax.value = parts.yMax;
    }
    // Keep hanger opacity in sync every frame during dissolve
    if (hangerVisible) {
      parts.hangers.traverse((o) => {
        const n = o.name.toLowerCase();
        if (!(o as THREE.Mesh).isMesh || !n.includes('hanger_a')) return;
        const mesh = o as THREE.Mesh;
        const mats = Array.isArray(mesh.material)
          ? mesh.material
          : [mesh.material];
        for (const m of mats) {
          const sm = m as THREE.MeshStandardMaterial;
          if (!sm.isMeshStandardMaterial) continue;
          sm.opacity = hangerOpacity;
          sm.depthWrite = hangerOpacity > 0.85;
        }
      });
    }
  });

  const decalMatProps = {
    transparent: true,
    depthTest: false,
    depthWrite: false,
    toneMapped: true as const,
    alphaTest: 0.05,
    polygonOffset: true,
    polygonOffsetFactor: -8,
    polygonOffsetUnits: -4,
    roughness: mobile ? 0.42 : 0.55,
    metalness: mobile ? 0.12 : 0.08,
    envMapIntensity: mobile ? 0.85 : 0.55,
  };

  /** Same rising dissolve as the cloth (per-vertex Y) — print peels with the jersey. */
  const bindDecalMat = (mat: THREE.MeshStandardMaterial | null) => {
    if (!mat || mat.userData.dissolveHooked) return;
    mat.userData.dissolveHooked = true;
    const u = {
      uDissolve: { value: dissolve },
      uYMin: { value: parts.yMin },
      uYMax: { value: parts.yMax },
      uUseFixedY: { value: 0 },
      uFixedY: { value: 0 },
    };
    mat.userData.dissolveU = u;
    applyClothDissolveShader(mat, u);
    decalMats.current.push(mat);
  };

  return (
    <group scale={scale}>
      {/* Hook fades out on the same dissolve clock as the cloth */}
      {hangerVisible && <RodHook dissolve={dissolve} />}
      <group position={[0, parts.lift, 0]}>
        <primitive object={parts.hangers} />
        {dustActive && parts.shirtGeo && (
          <JerseyDustParticles
            geometry={parts.shirtGeo}
            dissolve={dissolve}
            active={dustActive}
          />
        )}
        {clothVisible && parts.shirtGeo && parts.shirtMat && (
          <mesh
            geometry={parts.shirtGeo}
            material={parts.shirtMat}
            castShadow={dissolve < 0.85}
            receiveShadow
            frustumCulled={false}
            onAfterRender={() => {
              if (clothDrawn.current) return;
              clothDrawn.current = true;
              setPrintsReady(true);
            }}
          >
            {printsReady && (
              <>
            <Decal
              key={`name-${label}`}
              position={parts.namePos}
              rotation={Math.PI}
              scale={parts.nameScale}
              map={nameMap}
              depthTest={false}
              polygonOffsetFactor={-8}
            >
              <meshStandardMaterial
                map={nameMap}
                {...decalMatProps}
                ref={bindDecalMat}
              />
            </Decal>
            <Decal
              key={`num-${num}`}
              position={parts.numPos}
              rotation={Math.PI}
              scale={parts.numScale}
              map={numberMap}
              depthTest={false}
              polygonOffsetFactor={-8}
            >
              <meshStandardMaterial
                map={numberMap}
                {...decalMatProps}
                ref={bindDecalMat}
              />
            </Decal>
            <Decal
              position={parts.collarPos}
              rotation={Math.PI}
              scale={parts.collarScale}
              map={collarMap}
              depthTest={false}
              polygonOffsetFactor={-6}
            >
              <meshStandardMaterial
                map={collarMap}
                {...decalMatProps}
                ref={bindDecalMat}
              />
            </Decal>
            <Decal
              position={parts.cuffL}
              rotation={Math.PI}
              scale={parts.cuffScale}
              map={cuffMap}
              depthTest={false}
              polygonOffsetFactor={-6}
            >
              <meshStandardMaterial
                map={cuffMap}
                {...decalMatProps}
                ref={bindDecalMat}
              />
            </Decal>
            <Decal
              position={parts.cuffR}
              rotation={Math.PI}
              scale={parts.cuffScale}
              map={cuffMap}
              depthTest={false}
              polygonOffsetFactor={-6}
            >
              <meshStandardMaterial
                map={cuffMap}
                {...decalMatProps}
                ref={bindDecalMat}
              />
            </Decal>
              </>
            )}
          </mesh>
        )}
      </group>
    </group>
  );
}

useGLTF.preload(SHIRT_GLB_URL);
